"use client";

import {
  ArrowRight,
  CheckCircle,
  Eye,
  FileCheck,
  Globe,
  Key,
  Lock,
  Server,
  Shield,
} from "lucide-react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
} from "@/components/ui/motion";

const securityFeatures = [
  {
    icon: Lock,
    title: "Encryption at rest & in transit",
    description:
      "All data encrypted using AES-256 at rest and TLS 1.3 in transit. Your workflows and data are protected end-to-end.",
  },
  {
    icon: Key,
    title: "Secrets management",
    description:
      "API keys and credentials stored in isolated, encrypted vaults. Never exposed in logs or UI.",
  },
  {
    icon: Eye,
    title: "Least privilege access",
    description:
      "Role-based permissions ensure users only access what they need. Fine-grained controls for teams.",
  },
  {
    icon: FileCheck,
    title: "Audit logs",
    description:
      "Complete audit trail of all changes, access, and executions. Searchable and exportable.",
  },
  {
    icon: Server,
    title: "Infrastructure security",
    description:
      "Hosted on enterprise-grade cloud infrastructure with automatic security patches and updates.",
  },
  {
    icon: Globe,
    title: "Data residency",
    description:
      "Choose where your data is stored. EU and US regions available.",
  },
];

const compliance = [
  { label: "SOC 2 Type II", status: "In progress" },
  { label: "GDPR", status: "Compliant" },
  { label: "CCPA", status: "Compliant" },
  { label: "HIPAA", status: "Coming soon" },
];

const practices = [
  "Regular third-party security audits",
  "Vulnerability disclosure program",
  "24/7 security monitoring",
  "Incident response procedures",
  "Employee security training",
  "Secure development lifecycle",
];

export default function SecurityPage() {
  const navigate = useRouter();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-success/5 to-transparent pointer-events-none" />
        <div className="absolute top-20 left-1/3 w-[500px] h-[500px] bg-success/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <FadeInUp className="max-w-3xl mx-auto text-center">
            <motion.div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/30 mb-6 shadow-lg shadow-primary/10"
              whileHover={{ scale: 1.05, rotate: 5 }}
            >
              <Shield className="w-8 h-8 text-primary" />
            </motion.div>
            <h1 className="text-foreground mb-6">
              Security-first by
              <br />
              <span className="gradient-text">design</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Your workflows handle sensitive data. We treat security as a core
              feature, not an afterthought. Enterprise-grade protection for
              every workflow.
            </p>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate.push("/contact")}
            >
              Request security review
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </FadeInUp>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 lg:py-28 section-elevated">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="text-center mb-14">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              How we protect your data
            </h2>
            <p className="text-lg text-muted-foreground">
              Multiple layers of security at every level
            </p>
          </FadeInUp>

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {securityFeatures.map((feature, index) => (
              <StaggerItem key={index}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className="h-full p-6 rounded-xl card-interactive hover:border-primary/30"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
              <FadeInUp>
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Compliance & certifications
                </h2>
                <p className="text-muted-foreground mb-8">
                  We're committed to meeting the highest security standards and
                  compliance requirements.
                </p>
                <div className="space-y-4">
                  {compliance.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 rounded-lg bg-card border border-border"
                    >
                      <span className="font-medium text-foreground">
                        {item.label}
                      </span>
                      <span
                        className={`text-sm px-2.5 py-1 rounded-full ${
                          item.status === "Compliant"
                            ? "bg-success/10 text-success"
                            : item.status === "In progress"
                              ? "bg-warning/10 text-warning"
                              : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
              </FadeInUp>

              <FadeInUp delay={0.1}>
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Security practices
                </h2>
                <p className="text-muted-foreground mb-8">
                  Our security program includes comprehensive policies and
                  procedures.
                </p>
                <div className="space-y-3">
                  {practices.map((practice, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                      <span className="text-foreground">{practice}</span>
                    </div>
                  ))}
                </div>
              </FadeInUp>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 section-elevated">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInUp className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Have security questions?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Our security team is happy to answer questions and provide
              additional documentation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="gradient"
                size="lg"
                onClick={() => navigate.push("/contact")}
              >
                Contact security team
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate.push("/register")}
              >
                Start building free
              </Button>
            </div>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
}
